import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DriverClient from '../../clients/DriverClient';
import { type DriverRaceDataResponse } from '../../models/DriverRaceDataResponse';
import { formatMilliseconds, formatDateOnlyLocal } from '../../utils/time';
import { formatLocation } from '../../utils/bookingUtils';
import { FilterCommonResponse } from '../../models/FilterCommonResponse';
import CustomFilterDropdown from '../CustomFilterDropdown';
import EmptyState from '../EmptyState';

interface RecentActivityProps {
  driverId: string | undefined;
  take?: number;
}

const RecentActivity: React.FC<RecentActivityProps> = ({ driverId, take = 10 }) => {
  const navigate = useNavigate();
  const [races, setRaces] = useState<DriverRaceDataResponse[]>([]);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [availableCars, setAvailableCars] = useState<FilterCommonResponse[]>([]);
  const [availableTracks, setAvailableTracks] = useState<FilterCommonResponse[]>([]);
  const [selectedCar, setSelectedCar] = useState<FilterCommonResponse>();
  const [selectedTrack, setSelectedTrack] = useState<FilterCommonResponse>();

  const loadFilterOptions = async () => {
    if (!driverId) return;
    try {
      const [cars, tracks] = await Promise.all([
        DriverClient.getFilterCars(selectedTrack?.id ?? undefined),
        DriverClient.getFilterTracks(selectedCar?.id ?? undefined)
      ]);
      setAvailableCars(cars);
      setAvailableTracks(tracks);
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  };

  useEffect(() => {
    const fetchRaces = async () => {
      if (!driverId) return;
      setError('');
      setIsLoading(true);
      try {
        const result = await DriverClient.getUserRaces({
          take,
          skip,
          car: selectedCar?.id ?? undefined,
          track: selectedTrack?.id ?? undefined
        });
        setRaces(result.data);
        setTotalCount(result.totalCount);
        setIsLoading(false);
      } catch (err) {
        setError('Failed to load recent activity');
        setIsLoading(false);
      }
    };
    fetchRaces();
  }, [driverId, selectedCar, selectedTrack, take, skip]);

  const canPrev = skip > 0;
  const canNext = skip + races.length < totalCount;
  const currentPage = Math.floor(skip / take) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / take));

  const goPrev = () => {
    if (!canPrev) return;
    setSkip(Math.max(0, skip - take));
  };

  const goNext = () => {
    if (!canNext) return;
    setSkip(skip + take);
  };

  const handleFilterFocus = () => {
    loadFilterOptions();
  };

  const handleTrackChange = (value: string) => {
    const track = availableTracks.find(t => t.value === value);
    setSelectedTrack(track);
    setSkip(0);
  };

  const handleCarChange = (value: string) => {
    const car = availableCars.find(c => c.value === value);
    setSelectedCar(car);
    setSkip(0);
  };

  return (
    <div className="card card-bottom-radius h-100 recent-activity-card">
      <div className="card-body">
        {error && (
          <div className="alert alert-danger" role="alert">{error}</div>
        )}

        {isLoading && (
          <EmptyState message="Loading recent activity..." animate={true} />
        )}

        {!error && !isLoading && races.length === 0 && (
          <EmptyState />
        )}

        {!error && !isLoading && races.length > 0 && (
          <>
            <div className="mobile-filter-controls">
              <div className="mobile-filter-item">
                <span className="mobile-filter-label">Track</span>
                <CustomFilterDropdown
                  options={availableTracks}
                  value={selectedTrack?.value || ''}
                  onChange={handleTrackChange}
                  onFocus={handleFilterFocus}
                  ariaLabel="Filter by track"
                  placeholder="All"
                />
              </div>
              <div className="mobile-filter-item">
                <span className="mobile-filter-label">Car</span>
                <CustomFilterDropdown
                  options={availableCars}
                  value={selectedCar?.value || ''}
                  onChange={handleCarChange}
                  onFocus={handleFilterFocus}
                  ariaLabel="Filter by car"
                  placeholder="All"
                />
              </div>
            </div>
            <div className="desktop-only">
              <div className="table-responsive recent-activity-table">
                <table className="table table-sm table-hover table-custom align-middle">
                  <thead>
                    <tr>
                      <th className="text-center">Date</th>
                      <th className="text-center">Location</th>
                      <th className="text-center filterable-header">
                        <div className="header-with-filter">
                          <span>Track</span>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className={`filter-icon ${selectedTrack ? 'active' : ''}`} viewBox="0 0 16 16">
                            <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5zm1 .5v1.308l4.372 4.858A.5.5 0 0 1 7 8.5v5.306l2-.666V8.5a.5.5 0 0 1 .128-.334L13.5 3.308V2z" />
                          </svg>
                        </div>
                      </th>
                      <th className="text-center filterable-header">
                        <div className="header-with-filter">
                          <span>Car</span>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className={`filter-icon ${selectedCar ? 'active' : ''}`} viewBox="0 0 16 16">
                            <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5zm1 .5v1.308l4.372 4.858A.5.5 0 0 1 7 8.5v5.306l2-.666V8.5a.5.5 0 0 1 .128-.334L13.5 3.308V2z" />
                          </svg>
                        </div>
                      </th>

                      <th className="text-center">Sec1</th>
                      <th className="text-center">Sec2</th>
                      <th className="text-center">Sec3</th>
                      <th className="text-center">Time</th>
                      <th className="text-center">Valid</th>
                    </tr>
                    <tr className="filter-row desktop-filter-row">
                      <th></th>
                      <th></th>
                      <th className="text-start desktop-filter-cell">
                        <CustomFilterDropdown
                          options={availableTracks}
                          value={selectedTrack?.value || ''}
                          onChange={handleTrackChange}
                          onFocus={handleFilterFocus}
                          ariaLabel="Filter by track"
                          placeholder="All"
                        />
                      </th>
                      <th className="text-start desktop-filter-cell">
                        <CustomFilterDropdown
                          options={availableCars}
                          value={selectedCar?.value || ''}
                          onChange={handleCarChange}
                          onFocus={handleFilterFocus}
                          ariaLabel="Filter by car"
                          placeholder="All"
                        />
                      </th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th className="text-center"></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {races.map((r) => (
                      <tr
                        key={(r.id ?? '') + r.date + r.track}
                        onClick={() => r.id && r.raceId && navigate(`/dashboard/telemetry/${r.raceId}/${r.id}`, {
                          state: { track: r.track, layout: r.layout, car: r.car, date: r.date, location: r.location }
                        })}
                        style={{ cursor: r.id && r.raceId ? 'pointer' : 'default' }}
                        className={r.id && r.raceId ? 'clickable-row' : ''}
                      >
                        <td className="text-center">{formatDateOnlyLocal(r.date)}</td>
                        <td className="text-center">{formatLocation(r.location)}</td>
                        <td className="text-center">{r.track}</td>
                        <td className="text-center">{r.car}</td>
                        <td className="text-center time-value">{formatMilliseconds(r.sec1)}</td>
                        <td className="text-center time-value">{formatMilliseconds(r.sec2)}</td>
                        <td className="text-center time-value">{formatMilliseconds(r.sec3)}</td>
                        <td className="text-center time-value">{formatMilliseconds(r.time)}</td>
                        <td className="text-center">
                          <span
                            className={`valid-indicator ${r.valid ? 'valid' : 'invalid'}`}
                            title={r.valid ? 'Valid' : 'Invalid'}
                          />
                        </td>
                      </tr>
                    ))}
                    {Array.from({ length: Math.max(0, take - races.length) }).map((_, idx) => (
                      <tr key={`empty-${idx}`} style={{ visibility: 'hidden', height: '56px' }}>
                        <td colSpan={9}></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-between align-items-center mt-3 pagination-container">
                <small className="pagination-info">
                  Showing {skip + 1}-{skip + races.length} of {totalCount}
                </small>
                <div className="btn-group btn-group-sm">
                  <button className="btn btn-outline-secondary" onClick={goPrev} disabled={!canPrev}>
                    Previous
                  </button>
                  <button className="btn btn-outline-secondary active" disabled>
                    Page {currentPage} / {totalPages}
                  </button>
                  <button className="btn btn-outline-secondary" onClick={goNext} disabled={!canNext}>
                    Next
                  </button>
                </div>
              </div>
            </div>

            <div className="mobile-only">
              <div className="mobile-race-cards">
                {races.map((r) => (
                  <div
                    className={`mobile-race-card ${r.id && r.raceId ? 'clickable-card' : ''}`}
                    key={`mobile-${(r.id ?? '') + r.date + r.track}`}
                    onClick={() => r.id && r.raceId && navigate(`/dashboard/telemetry/${r.raceId}/${r.id}`, {
                      state: { track: r.track, layout: r.layout, car: r.car, date: r.date, location: r.location }
                    })}
                  >
                    <div className="mobile-card-header">
                      <div className="mobile-card-title">{r.track}</div>
                      <span className={`mobile-valid-chip ${r.valid ? 'valid' : 'invalid'}`}>
                        {r.valid ? 'Valid' : 'Invalid'}
                      </span>
                    </div>
                    <div className="mobile-card-subheader">
                      <span className="mobile-card-date">{formatDateOnlyLocal(r.date)}</span>
                      <span className="mobile-card-divider">•</span>
                      <span className="mobile-card-location">{formatLocation(r.location)}</span>
                    </div>
                    <div className="mobile-card-section">
                      <span className="mobile-card-label accent">Car</span>
                      <span className="mobile-card-value accent">{r.car}</span>
                    </div>
                    <div className="mobile-card-section">
                      <span className="mobile-card-label accent">Time</span>
                      <span className="mobile-total-value accent">{formatMilliseconds(r.time)}</span>
                    </div>
                    <div className="mobile-card-metrics">
                      <div>
                        <span className="mobile-card-label">Sec1</span>
                        <span className="mobile-metric-value">{formatMilliseconds(r.sec1)}</span>
                      </div>
                      <div>
                        <span className="mobile-card-label">Sec2</span>
                        <span className="mobile-metric-value">{formatMilliseconds(r.sec2)}</span>
                      </div>
                      <div>
                        <span className="mobile-card-label">Sec3</span>
                        <span className="mobile-metric-value">{formatMilliseconds(r.sec3)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mobile-only mt-3 pagination-container mobile-pagination d-flex">
                <small className="pagination-info">
                  Showing {skip + 1}-{skip + races.length} of {totalCount}
                </small>
                <div className="btn-group btn-group-sm">
                  <button className="btn btn-outline-secondary" onClick={goPrev} disabled={!canPrev}>
                    Previous
                  </button>
                  <button className="btn btn-outline-secondary active" disabled>
                    Page {currentPage} / {totalPages}
                  </button>
                  <button className="btn btn-outline-secondary" onClick={goNext} disabled={!canNext}>
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecentActivity;

